import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { BaseRow, Columns } from '@openx/types/tableHelpers.ts';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParsedPdpError } from '../utils/parsePdpError.ts';

export const EXPAND_ICON_ATTRIBUTE_NAME = 'expandRow';
export const TDD_SYNC_ERROR_JSON_VIEW_STORAGE_KEY = 'tddErrorJsonView';

export type ErrorRow = ParsedPdpError & BaseRow & { index: number };

export const useTddErrorTableColumns = (errors?: ErrorRow[]) => {
  const { t } = useTranslation();
  const [expandedRowIds, setExpandedRowIds] = useState<Set<number>>(new Set());
  const [isCollapsableJsonView, setIsCollapsableJsonView] = useState(() => {
    const storedValue = localStorage.getItem(TDD_SYNC_ERROR_JSON_VIEW_STORAGE_KEY);

    return storedValue !== null ? storedValue === 'true' : true;
  });

  const areRowsExpanded = expandedRowIds.size > 0;

  const toggleExpandedRows = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      setExpandedRowIds(prevSet => {
        // If any rows are expanded, collapse all
        if (prevSet.size > 0) {
          return new Set();
        }

        // Otherwise, expand all rows
        const allRowIds = errors?.map(error => error.index) ?? [];
        return new Set(allRowIds);
      });
    },
    [errors]
  );

  const toggleCollapsableJsonView = useCallback(() => {
    setIsCollapsableJsonView(prevState => {
      const newValue = !prevState;

      localStorage.setItem(TDD_SYNC_ERROR_JSON_VIEW_STORAGE_KEY, String(newValue));

      return newValue;
    });
  }, []);

  const isForcedExpanded = useCallback(
    (errorRow: ErrorRow) => {
      return expandedRowIds.has(errorRow.index);
    },
    [expandedRowIds]
  );

  const toggleRowExpansion = useCallback((rowIndex: number) => {
    setExpandedRowIds(prevSet => {
      const newSet = new Set(prevSet);

      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }

      return newSet;
    });
  }, []);

  const expandAllRows = useCallback((indexes: number[]) => {
    setExpandedRowIds(new Set(indexes));
  }, []);

  const columns: Columns<ErrorRow> = useMemo(
    () => [
      {
        key: 'index',
        render: errorRow => {
          return (
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              {/* icon button and icon needs to have attribute 'data-expandicon' to expand ExpandableTableRow */}
              <IconButton
                data-expandicon={EXPAND_ICON_ATTRIBUTE_NAME}
                data-test="expand-icon"
                edge="start"
                size="small"
                sx={{
                  color: 'primary.main',
                  mr: 1,
                }}
              >
                {errorRow.expanded ? (
                  <ExpandLessIcon data-expandicon={EXPAND_ICON_ATTRIBUTE_NAME} />
                ) : (
                  <ExpandMoreIcon data-expandicon={EXPAND_ICON_ATTRIBUTE_NAME} />
                )}
              </IconButton>
              <Typography variant="body1Bold">{errorRow.index}.</Typography>
            </Box>
          );
        },
        title: (
          <>
            <Tooltip title={`${areRowsExpanded ? t('Collapse') : t('Expand')} ${t('All')}`}>
              <IconButton data-test="expand-rows" edge="start" size="small" onClick={toggleExpandedRows} sx={{ mr: 1 }}>
                {areRowsExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              </IconButton>
            </Tooltip>
            {t('No.')}
          </>
        ),
        width: '20%',
      },
      {
        key: 'message',
        render: errorRow => (
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
            }}
          >
            {errorRow.message || t('Unknown Error')}
          </Typography>
        ),
        title: t('Message'),
        width: '70%',
      },
      // that's a hacky way of adding a button to table header
      {
        key: 'jsonViewToggle',
        render: () => ' ',
        title: (
          <Tooltip
            title={
              isCollapsableJsonView
                ? t('Switch to full json data preview')
                : t('Switch to tree-structured data preview')
            }
          >
            <IconButton data-test="json-view-toggle" onClick={toggleCollapsableJsonView} size="small">
              {isCollapsableJsonView ? <DataObjectIcon fontSize="small" /> : <AccountTreeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        ),
        width: '10%',
      },
    ],
    [areRowsExpanded, isCollapsableJsonView, t, toggleCollapsableJsonView, toggleExpandedRows]
  );

  return {
    areRowsExpanded,
    columns,
    expandAllRows,
    isForcedExpanded,
    isCollapsableJsonView,
    toggleCollapsableJsonView,
    toggleExpandedRows,
    toggleRowExpansion,
  };
};
